/**
 * Dashboard panel: network reference (primary, backups, active L0) and manual handover
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAuth } from '../contexts/AuthContext';
import { useDevices } from '../contexts/DevicesContext';
import { networkApi } from '../services/networkApi';
import { referencesApi } from '../services/referencesApi';
import { Device } from '../types/Device';
import { DeviceRef, NetworkReference, RoleCard } from '../types/references';

// Server errors: 409 detail is string[], 422 detail is [{msg}], others a string
export const errorText = (error: any): string => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (d && typeof d === 'object' && 'msg' in d ? d.msg : String(d))).join('; ');
  }
  if (typeof detail === 'string') return detail;
  return error?.message || 'Request failed';
};

const isConflict = (error: any) => error?.response?.status === 409;

export const NETWORK_POLL_MS = 10_000;

const deviceLabel = (d: Device) => d.name || d.mqtt_id;

interface CandidateProps {
  label: string;
  candidate: DeviceRef;
  active: boolean;
  role: RoleCard | null | undefined;
  canActivate: boolean;
  onActivate: (c: DeviceRef) => void;
}

const Candidate: React.FC<CandidateProps> = ({ label, candidate, active, role, canActivate, onActivate }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600}>
      {candidate.key}
    </Typography>
    {active && <Chip size="small" color="success" label="active" />}
    {role && (
      <Chip
        size="small"
        variant="outlined"
        color={role.usable ? 'success' : 'warning'}
        label={role.usable ? 'usable' : role.reason || 'not usable'}
      />
    )}
    {canActivate && !active && (
      <Button size="small" variant="outlined" aria-label={`Activate ${candidate.key}`} onClick={() => onActivate(candidate)}>
        Activate
      </Button>
    )}
  </Stack>
);

interface ActivateDialogProps {
  target: DeviceRef | null;
  onClose: () => void;
  onDone: (net: NetworkReference) => void;
}

const ActivateDialog: React.FC<ActivateDialogProps> = ({ target, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [canForce, setCanForce] = useState(false);

  useEffect(() => {
    setMessage(null);
    setCanForce(false);
  }, [target]);

  const run = async (force: boolean) => {
    if (!target) return;
    setBusy(true);
    try {
      onDone(await networkApi.activate(target.id, force));
    } catch (error) {
      setMessage(errorText(error));
      // A 409 can be forced; once forcing, keep the button for another try
      setCanForce(force || isConflict(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={target !== null} onClose={busy ? undefined : onClose}>
      <DialogTitle>Network reference</DialogTitle>
      <DialogContent>
        <Typography>Switch the network reference to {target?.key}?</Typography>
        {message && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {canForce ? (
          <Button color="warning" variant="contained" onClick={() => run(true)} disabled={busy}>
            Force switch
          </Button>
        ) : (
          <Button variant="contained" onClick={() => run(false)} disabled={busy}>
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

interface EditDialogProps {
  open: boolean;
  net: NetworkReference;
  onClose: () => void;
  onDone: (net: NetworkReference) => void;
}

const EditDialog: React.FC<EditDialogProps> = ({ open, net, onClose, onDone }) => {
  const { devices } = useDevices();
  const [primaryId, setPrimaryId] = useState<number | null>(net.primary?.id ?? null);
  const [backupIds, setBackupIds] = useState<number[]>(net.backups.map((b) => b.id));
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPrimaryId(net.primary?.id ?? null);
      setBackupIds(net.backups.map((b) => b.id));
      setMessage(null);
    }
  }, [open, net]);

  const l0 = devices.filter((d) => d.level === 0);
  const l1 = devices.filter((d) => d.level === 1);
  // Keep the chosen order of the backups
  const selectedBackups = backupIds
    .map((id) => l1.find((d) => d.id === id))
    .filter((d): d is Device => d !== undefined);

  const save = async () => {
    setBusy(true);
    try {
      onDone(await networkApi.setNetworkReference({ primary_id: primaryId, backup_ids: backupIds }));
    } catch (error) {
      setMessage(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit network reference</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="network-primary-label">Primary</InputLabel>
            <Select
              labelId="network-primary-label"
              label="Primary"
              value={primaryId ?? ''}
              onChange={(e) => setPrimaryId(e.target.value === '' ? null : Number(e.target.value))}
            >
              <MenuItem value="">none</MenuItem>
              {l0.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {deviceLabel(d)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            options={l1}
            value={selectedBackups}
            getOptionLabel={deviceLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, devs) => setBackupIds(devs.map((d) => d.id))}
            renderInput={(params) => (
              <TextField {...params} label="Backups" size="small" helperText="L1 devices, in handover order" />
            )}
          />
          {message && <Alert severity="error">{message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const NetworkReferencePanel: React.FC = () => {
  const { hasRole } = useAuth();
  const [net, setNet] = useState<NetworkReference | null>(null);
  const [roles, setRoles] = useState<Record<number, RoleCard | null>>({});
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<DeviceRef | null>(null);
  const [editing, setEditing] = useState(false);

  const canActivate = hasRole('admin', 'operator');
  const canEdit = hasRole('admin');

  const loadRoles = useCallback(async (n: NetworkReference) => {
    const candidates = [n.primary, ...n.backups].filter((c): c is DeviceRef => c !== null);
    const entries = await Promise.all(
      candidates.map(async (c) => {
        try {
          return [c.id, (await referencesApi.getReferences(c.id)).role ?? null] as const;
        } catch {
          return [c.id, null] as const;
        }
      })
    );
    setRoles(Object.fromEntries(entries));
  }, []);

  const apply = useCallback(
    (n: NetworkReference) => {
      setNet(n);
      setError(null);
      loadRoles(n);
    },
    [loadRoles]
  );

  // Paused while a dialog is open: a refresh would reset the Edit form or the confirm target
  const dialogOpen = useRef(false);
  dialogOpen.current = target !== null || editing;

  useEffect(() => {
    const load = () =>
      networkApi
        .getNetworkReference()
        .then(apply)
        .catch((e) => setError(errorText(e)));
    load();
    const timer = setInterval(() => {
      if (!dialogOpen.current) load();
    }, NETWORK_POLL_MS);
    return () => clearInterval(timer);
  }, [apply]);

  const candidate = (label: string, c: DeviceRef) => (
    <Candidate
      key={`${label}-${c.id}`}
      label={label}
      candidate={c}
      active={net?.active?.id === c.id}
      role={roles[c.id]}
      canActivate={canActivate}
      onActivate={setTarget}
    />
  );

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Network reference
        </Typography>
        {canEdit && net && (
          <Button size="small" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {net && (
        <Stack spacing={1}>
          {net.primary ? candidate('Primary', net.primary) : <Typography variant="body2">Primary: none</Typography>}
          {net.backups.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Backups
            </Typography>
          )}
          {net.backups.map((b) => candidate('', b))}
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
              Active
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {net.active?.key ?? 'none'}
            </Typography>
          </Stack>
        </Stack>
      )}
      <ActivateDialog
        target={target}
        onClose={() => setTarget(null)}
        onDone={(n) => {
          setTarget(null);
          apply(n);
        }}
      />
      {net && (
        <EditDialog
          open={editing}
          net={net}
          onClose={() => setEditing(false)}
          onDone={(n) => {
            setEditing(false);
            apply(n);
          }}
        />
      )}
    </Paper>
  );
};

export default NetworkReferencePanel;
